import React, { createContext, useState, useEffect } from "react";

interface GlobalStateContextProps {
  isCookieEnabled: boolean;
  setCookieEnabled: (value: boolean) => void;
  language: string;
  setLanguage: (value: string) => void;
}

const GlobalStateContext = createContext<GlobalStateContextProps>({
  isCookieEnabled: false,
  setCookieEnabled: (value: boolean) => {},
  language: 'en',
  setLanguage: (value: string) => {},
});

interface GlobalStateProviderProps {
  children: React.ReactNode;
  initialLanguage?: string;
}

export const GlobalStateProvider: React.FC<GlobalStateProviderProps> = ({ children, initialLanguage }) => {
  const [cookies, setCookies] = useState(false);
  const [language, setLanguageState] = useState(initialLanguage || 'en');

  useEffect(() => {
    // Check if a language is stored in localStorage
    const storedLanguage = localStorage.getItem('language');

    if (storedLanguage) {
      // If a language is stored, use it
      setLanguageState(storedLanguage);
      return
    }
    // If no language is stored, use the browser's default language
    const browserLanguage = navigator.language.split("-")[0];
    if (browserLanguage === 'en') {
      setLanguageState('en');
      return
    }
    
    const currentUrl = window.location.pathname + window.location.search;
    const newUrl = new URL(currentUrl, window.location.origin);
    newUrl.searchParams.set('lang', browserLanguage);
    window.location.href = newUrl.toString();
  }, []);

  const setCookieEnabled = (value: boolean) => {
    setCookies(value);
  };

  const setLanguage = (value: string) => {
    localStorage.setItem('language', value);
    setLanguageState(value);
  };

  const globalState: GlobalStateContextProps = {
    isCookieEnabled: cookies,
    setCookieEnabled,
    language,
    setLanguage,
  };

  return (
    <GlobalStateContext.Provider value={globalState}>
      {children}
    </GlobalStateContext.Provider>
  );
};

export default GlobalStateContext;
